import React, { useState } from "react";
import Seo from "../components/Seo";
import { STATIC_PAGES } from "../seo/openGraph";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, CheckCircle, ArrowRight, Send } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  LEAD_CARD_CLASS,
  LEAD_FIELD_CLASS,
  LEAD_LABEL_CLASS,
  LEAD_SELECT_CLASS,
} from "../components/leadMagnetStyles";
import { ContactHeroVisual } from "../components/HeroVisuals";
import { trackLead } from "@/lib/analytics";
import {
  HoneypotField,
  LEAD_SUBMIT_ERROR,
  LeadFormError,
  honeypotValue,
  submitAppsContact,
  useLeadFormGuard,
} from "@/lib/hubspot";

const BUDGET_LABELS = {
  "25k-50k": "$25,000 - $50,000",
  "50k-100k": "$50,000 - $100,000",
  "100k-250k": "$100,000 - $250,000",
  "250k+": "$250,000+",
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" },
  }),
};

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const { companyUrl, setCompanyUrl, mountedAt } = useLeadFormGuard();
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", company: "", message: "", budget: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");
    setSubmitting(true);
    try {
      const outcome = await submitAppsContact(
        {
          email: formData.email,
          name: formData.name,
          phone: formData.phone,
          company: formData.company,
          message: formData.message,
          budget: BUDGET_LABELS[formData.budget] || formData.budget,
          source: "contact_form",
        },
        {
          companyUrl: honeypotValue(e, companyUrl),
          mountedAt,
          name: formData.name,
        },
      );
      if (outcome === "sent") trackLead("contact_form");
      setSubmitted(true);
    } catch {
      setError(LEAD_SUBMIT_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo {...STATIC_PAGES.contact} />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#0A0A0A]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div variants={fadeUp} initial="hidden" animate="visible">
              <p className="text-[#00AEEF] text-sm uppercase tracking-widest font-medium mb-4">Contact Us</p>
              <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6" data-testid="contact-heading">
                Talk to a senior engineer.
              </h1>
              <p className="text-lg text-neutral-300 max-w-xl">
                Send the product, the constraint, and the deadline. A senior engineer replies within one business day. hello@maxiomtech.com · 703-942-9420.
              </p>
            </motion.div>
            <ContactHeroVisual />
          </div>
        </div>
      </section>

      <section className="pb-20 md:pb-32 bg-[#0A0A0A]" data-testid="contact-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Form */}
            <div className="lg:col-span-3">
              {submitted ? (
                <motion.div
                  variants={fadeUp}
                  initial="hidden"
                  animate="visible"
                  className={`${LEAD_CARD_CLASS} rounded-xl p-12 text-center`}
                  data-testid="contact-thank-you"
                >
                  <CheckCircle className="w-16 h-16 text-[#00AEEF] mx-auto mb-6" />
                  <h2 className="text-3xl font-bold text-white mb-4">Thank You!</h2>
                  <p className="text-gray-200 text-lg leading-relaxed max-w-md mx-auto">
                    We've received your message. A senior engineer will read it and reply within one business day.
                  </p>
                </motion.div>
              ) : (
                <motion.form
                  variants={fadeUp}
                  initial="hidden"
                  animate="visible"
                  custom={1}
                  onSubmit={handleSubmit}
                  className={`${LEAD_CARD_CLASS} rounded-xl p-8 md:p-10 space-y-6`}
                  data-testid="contact-form"
                  data-hs-do-not-collect="true"
                  aria-busy={submitting}
                >
                  <HoneypotField value={companyUrl} onChange={setCompanyUrl} />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className={LEAD_LABEL_CLASS}>Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className={LEAD_FIELD_CLASS}
                        placeholder="John Smith"
                        data-testid="contact-input-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className={LEAD_LABEL_CLASS}>Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className={LEAD_FIELD_CLASS}
                        placeholder="john@company.com"
                        data-testid="contact-input-email"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className={LEAD_LABEL_CLASS}>Phone</Label>
                      <Input
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className={LEAD_FIELD_CLASS}
                        placeholder="(555) 123-4567"
                        data-testid="contact-input-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company" className={LEAD_LABEL_CLASS}>Company</Label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className={LEAD_FIELD_CLASS}
                        placeholder="Acme Corp"
                        data-testid="contact-input-company"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="budget" className={LEAD_LABEL_CLASS}>Estimated Budget</Label>
                    <select
                      id="budget"
                      name="budget"
                      value={formData.budget}
                      onChange={handleChange}
                      className={LEAD_SELECT_CLASS}
                      data-testid="contact-select-budget"
                    >
                      <option value="" className="bg-[#121216]">Select a range</option>
                      <option value="25k-50k" className="bg-[#121216]">$25,000 - $50,000</option>
                      <option value="50k-100k" className="bg-[#121216]">$50,000 - $100,000</option>
                      <option value="100k-250k" className="bg-[#121216]">$100,000 - $250,000</option>
                      <option value="250k+" className="bg-[#121216]">$250,000+</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message" className={LEAD_LABEL_CLASS}>Tell us about your project *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      className={`${LEAD_FIELD_CLASS} resize-none`}
                      placeholder="Describe your project, goals, timeline..."
                      data-testid="contact-textarea-message"
                    />
                  </div>
                  <LeadFormError message={error} testId="contact-form-error" />
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#00AEEF] hover:bg-[#0095CC] text-white font-medium py-6 text-base rounded-md transition-all duration-300 shadow-[0_0_20px_rgba(0,174,239,0.3)] hover:shadow-[0_0_30px_rgba(0,174,239,0.5)]"
                    data-testid="contact-submit-button"
                  >
                    {submitting ? "Sending..." : "Send Message"}{" "}
                    {!submitting && <Send className="ml-2 w-5 h-5" />}
                  </Button>
                </motion.form>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2 space-y-8">
              <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={2}>
                <div className="bg-[#121212] border border-white/5 rounded-xl p-8 space-y-6">
                  <h3 className="text-xl font-semibold text-white">Get in Touch</h3>
                  <div className="space-y-4">
                    <a href="tel:7039429420" className="flex items-center gap-4 text-gray-400 hover:text-[#00AEEF] transition-colors" data-testid="contact-sidebar-phone">
                      <div className="w-10 h-10 rounded-lg bg-[#00AEEF]/10 flex items-center justify-center flex-shrink-0">
                        <Phone className="w-5 h-5 text-[#00AEEF]" />
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Phone</div>
                        <div className="text-sm text-white">703-942-9420</div>
                      </div>
                    </a>
                    <a href="mailto:hello@maxiomtech.com" className="flex items-center gap-4 text-gray-400 hover:text-[#00AEEF] transition-colors" data-testid="contact-sidebar-email">
                      <div className="w-10 h-10 rounded-lg bg-[#00AEEF]/10 flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-[#00AEEF]" />
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Email</div>
                        <div className="text-sm text-white">hello@maxiomtech.com</div>
                      </div>
                    </a>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-[#00AEEF]/10 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-[#00AEEF]" />
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Location</div>
                        <div className="text-sm text-white">
                          Maxiom Technology<br />
                          44927 George Washington Blvd<br />
                          Suite 265<br />
                          Ashburn, VA 20147
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={3}>
                <div className="bg-[#121212] border border-white/5 rounded-xl p-8" data-testid="contact-why-section">
                  <h3 className="text-xl font-semibold text-white mb-6">Why Maxiom Apps?</h3>
                  <ul className="space-y-4">
                    {[
                      "App studio since 2002",
                      "$100M+ contract value · 221+ clients",
                      "Clutch-rated (AT&T, Microsoft, BMW, Toastmasters)",
                      "Ashburn, VA (DC Metro). We build from here",
                      "A senior engineer, not a BDR script",
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-[#00AEEF] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-400 text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
